import React from 'react';
import { getBadgeImage, getBadgeName } from '../../data/badgeImages';

interface BadgeGalleryProps {
  booksOverview: any;
  childId: number;
}

/**
 * Componente que muestra todas las insignias disponibles en una galería
 */
const BadgeGallery: React.FC<BadgeGalleryProps> = ({ booksOverview, childId }) => {
  const books = [1, 2, 3, 4, 5, 6, 7, 8];
  const activitiesPerBook = 7;
  
  // Función para determinar si una insignia está desbloqueada
  // Ahora usa la información real del niño desde la API
  const isBadgeUnlocked = (bookNumber: number, activityNumber: number): boolean => {
    // Si tenemos datos reales del niño, usarlos
    if (booksOverview && booksOverview.books) {
      const book = booksOverview.books.find((b: any) => b.book_number === bookNumber);
      if (!book || !book.is_unlocked) {
        return false;
      }
      
      const activity = book.activities?.find((a: any) => a.number === activityNumber);
      if (!activity) {
        return false;
      }
      
      // La insignia está desbloqueada si la actividad está aprobada
      return activity.status === 'approved';
    }
    
    // Fallback: si no hay datos del niño, simular un estado más realista
    // Solo las primeras 3 actividades del primer libro están desbloqueadas por defecto
    if (bookNumber === 1 && activityNumber <= 3) {
      return true;
    }
    
    return false;
  };

  // Función para determinar si un libro está desbloqueado
  const isBookUnlocked = (bookNumber: number): boolean => {
    if (booksOverview && booksOverview.books) {
      const book = booksOverview.books.find((b: any) => b.book_number === bookNumber);
      return book ? book.is_unlocked : false;
    }
    
    // Fallback: solo el primer libro está desbloqueado por defecto
    return bookNumber === 1;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        🏆 Galería de Insignias Disponibles
      </h3>
      
      {/* Mensaje informativo cuando no hay datos del niño */}
      {(!booksOverview || !booksOverview.books) && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm text-center">
            💡 Mostrando vista de ejemplo. Para ver tus insignias reales, accede desde tu perfil de niño.
          </p>
        </div>
      )}
      
      <div className="space-y-8">
        {books.map((bookNumber) => (
          <div key={bookNumber} className="border-b border-gray-200 pb-6 last:border-b-0">
            <h4 className={`text-lg font-semibold mb-4 flex items-center ${
              isBookUnlocked(bookNumber) ? 'text-gray-700' : 'text-gray-400'
            }`}>
              <span className="text-2xl mr-3">
                {bookNumber === 1 ? '🍬' : 
                 bookNumber === 2 ? '🌳' : 
                 bookNumber === 3 ? '💨' : 
                 bookNumber === 4 ? '🦅' : 
                 bookNumber === 5 ? '🤝' : 
                 bookNumber === 6 ? '🏗️' : 
                 bookNumber === 7 ? '💪' : '💰'}
              </span>
              Libro {bookNumber}
              {!isBookUnlocked(bookNumber) && (
                <span className="ml-2 text-sm bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                  🔒 Bloqueado
                </span>
              )}
            </h4>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {Array.from({ length: activitiesPerBook }, (_, i) => {
                const activityNumber = i + 1;
                const badgeImage = getBadgeImage(bookNumber, activityNumber);
                const badgeName = getBadgeName(bookNumber, activityNumber);
                
                if (!badgeImage || badgeImage === '') return null;
                
                const isUnlocked = isBadgeUnlocked(bookNumber, activityNumber);
                const bookUnlocked = isBookUnlocked(bookNumber);
                
                return (
                  <div key={activityNumber} className="text-center group">
                    <div className="relative mb-2">
                      <img 
                        src={badgeImage} 
                        alt={`Insignia ${badgeName}`}
                        className={`w-16 h-16 rounded-full border-2 shadow-sm transition-all duration-200 group-hover:scale-110 ${
                          isUnlocked 
                            ? 'border-gray-200 group-hover:border-blue-400' 
                            : bookUnlocked
                            ? 'border-gray-400 grayscale opacity-50'
                            : 'border-gray-300 grayscale opacity-30'
                        }`}
                        title={isUnlocked ? badgeName : `${badgeName} (No desbloqueada)`}
                      />
                      {/* Número de actividad */}
                      <div className={`absolute -top-1 -right-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold ${
                        isUnlocked ? 'bg-blue-500' : bookUnlocked ? 'bg-gray-500' : 'bg-gray-400'
                      }`}>
                        {activityNumber}
                      </div>
                    </div>
                    <p className={`text-xs font-medium leading-tight ${
                      isUnlocked ? 'text-gray-600' : bookUnlocked ? 'text-gray-400' : 'text-gray-300'
                    }`}>
                      {badgeName}
                    </p>
                    {/* Indicador de estado */}
                    {!isUnlocked && (
                      <div className={`text-xs mt-1 ${
                        bookUnlocked ? 'text-gray-400' : 'text-gray-300'
                      }`}>
                        {bookUnlocked ? '🔒 No desbloqueada' : '📚 Libro bloqueado'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      
      {/* Información sobre el sistema de insignias */}
      <div className="mt-8 text-center">
        <div className="bg-blue-50 rounded-lg p-4 max-w-2xl mx-auto">
          <h4 className="font-semibold text-blue-800 mb-2">💡 Cómo funciona el sistema de insignias</h4>
          <div className="text-sm text-blue-700 space-y-1">
            <p>• <span className="font-medium">Insignias brillantes:</span> Actividades completadas y aprobadas</p>
            <p>• <span className="font-medium">Insignias en gris:</span> Actividades del libro desbloqueado pero no completadas</p>
            <p>• <span className="font-medium">Insignias muy opacas:</span> Actividades de libros bloqueados</p>
            <p>• <span className="font-medium">Números azules:</span> Actividades desbloqueadas</p>
            <p>• <span className="font-medium">Números grises:</span> Actividades no desbloqueadas</p>
            <p>• <span className="font-medium">Números muy claros:</span> Actividades de libros bloqueados</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeGallery;
